// src/pages/ProductSummaryPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Loader2, AlertCircle, ZoomIn, ZoomOut, Bookmark, ChevronRight, ChevronsRight, ChevronsLeft } from 'lucide-react';
import apiClient from '../api/apiClient';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { convertToPersianNumbers } from '../utils/PersianNumberUtils'; // ← اضافه شد

// ----------------------------------------------------------------------
// توابع تبدیل HTML به بلوک و صفحه‌بندی (دقیقاً مانند قبل)
// ----------------------------------------------------------------------
const splitHtmlToBlocks = (html) => {
  if (!html) return [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;
  const blocks = [];
  const processNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.trim();
      if (text) blocks.push(`<span>${text}</span>`);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();
      const isBlock = ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'pre'].includes(tagName);
      if (isBlock) {
        blocks.push(node.outerHTML);
      } else {
        blocks.push(node.outerHTML);
        node.childNodes.forEach(child => processNode(child));
      }
    }
  };
  body.childNodes.forEach(child => processNode(child));
  return blocks;
};

const countWordsInHtml = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  const text = tmp.textContent || tmp.innerText || '';
  return text.split(/\s+/).filter(w => w.length > 0).length;
};

const groupBlocksIntoPages = (blocks, wordsPerPage = 300) => {
  const pages = [];
  let currentPageBlocks = [];
  let currentWordCount = 0;
  for (const block of blocks) {
    const blockWordCount = countWordsInHtml(block);
    if (currentWordCount + blockWordCount > wordsPerPage && currentPageBlocks.length > 0) {
      pages.push(currentPageBlocks.join('\n'));
      currentPageBlocks = [];
      currentWordCount = 0;
    }
    currentPageBlocks.push(block);
    currentWordCount += blockWordCount;
  }
  if (currentPageBlocks.length > 0) pages.push(currentPageBlocks.join('\n'));
  return pages.length ? pages : ['<p>متن موجود نیست.</p>'];
};

// تابع تبدیل اعداد انگلیسی به فارسی در متن HTML
const persianizeHtmlNumbers = (htmlString) => {
  if (!htmlString) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null, false);
  const persianDigits = ['۰','۱','۲','۳','۴','۵','۶','۷','۸','۹'];
  let node;
  while ((node = walker.nextNode())) {
    node.textContent = node.textContent.replace(/[0-9]/g, (match) => persianDigits[parseInt(match)]);
  }
  return doc.body.innerHTML;
};

// ----------------------------------------------------------------------
// کامپوننت صفحه‌بندی (با اعداد فارسی)
// ----------------------------------------------------------------------
const PaginationControl = ({ currentPage, totalPages, onPageChange }) => {
  const [inputPage, setInputPage] = useState(currentPage + 1);
  useEffect(() => {
    setInputPage(currentPage + 1);
  }, [currentPage]);

  const handleGoToPage = () => {
    let page = parseInt(inputPage);
    if (isNaN(page)) page = 1;
    page = Math.max(1, Math.min(page, totalPages));
    onPageChange(page - 1);
    setInputPage(page);
  };

  // اعداد فارسی برای نمایش
  const persianCurrent = convertToPersianNumbers(currentPage + 1);
  const persianTotal = convertToPersianNumbers(totalPages);

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 my-4 bg-green-50/60 p-2 rounded-lg shadow-sm border border-green-100">
      <button onClick={() => onPageChange(0)} disabled={currentPage === 0} className="p-1 rounded hover:bg-green-100 disabled:opacity-40"><ChevronsRight size={20} /></button>
      <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 0} className="p-1 rounded hover:bg-green-100 disabled:opacity-40"><ChevronRight size={20} /></button>
      <span className="text-sm text-gray-700">صفحه {persianCurrent} از {persianTotal}</span>
      <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages - 1} className="p-1 rounded hover:bg-green-100 disabled:opacity-40"><ChevronLeft size={20} /></button>
      <button onClick={() => onPageChange(totalPages - 1)} disabled={currentPage === totalPages - 1} className="p-1 rounded hover:bg-green-100 disabled:opacity-40"><ChevronsLeft size={20} /></button>
      <div className="flex items-center gap-1 mr-4">
        <input
          type="number"
          value={inputPage}
          onChange={(e) => setInputPage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleGoToPage()}
          className="w-16 text-center border border-green-200 rounded px-2 py-1 text-sm bg-white"
          min={1}
          max={totalPages}
        />
        <button onClick={handleGoToPage} className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700">برو</button>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------
// کامپوننت اصلی
// ----------------------------------------------------------------------
const ProductSummaryPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('summaryFontSize');
    return saved ? parseInt(saved) : 16;
  });
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [bookmark, setBookmark] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!slug) return;
      setLoading(true);
      try {
        const response = await apiClient.get(`/products/${slug}/`);
        const prod = response.data;
        setProduct(prod);
        const blocks = splitHtmlToBlocks(prod.long_description || '');
        const htmlPages = groupBlocksIntoPages(blocks, 300);
        setPages(htmlPages);
        setTotalPages(htmlPages.length);
        const savedPage = localStorage.getItem(`summary_page_${slug}`);
        setCurrentPage(savedPage ? parseInt(savedPage) : 0);
      } catch (err) {
        setError('امکان بارگذاری چکیده محتوا وجود ندارد.');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug]);

  useEffect(() => {
    if (slug) localStorage.setItem(`summary_page_${slug}`, currentPage);
  }, [currentPage, slug]);

  useEffect(() => {
    if (!slug) return;
    const savedBookmark = localStorage.getItem(`bookmark_${slug}`);
    if (savedBookmark !== null) setBookmark(parseInt(savedBookmark));
  }, [slug]);

  useEffect(() => {
    if (slug && bookmark !== null) localStorage.setItem(`bookmark_${slug}`, bookmark);
    else if (slug && bookmark === null) localStorage.removeItem(`bookmark_${slug}`);
  }, [bookmark, slug]);

  const increaseFont = () => {
    if (fontSize < 28) {
      setFontSize(fontSize + 2);
      localStorage.setItem('summaryFontSize', fontSize + 2);
    }
  };
  const decreaseFont = () => {
    if (fontSize > 12) {
      setFontSize(fontSize - 2);
      localStorage.setItem('summaryFontSize', fontSize - 2);
    }
  };

  const setBookmarkHere = () => {
    setBookmark(currentPage);
    const persianPage = convertToPersianNumbers(currentPage + 1);
    alert(`بوکمارک در صفحه ${persianPage} ثبت شد.`);
  };

  const goToBookmark = () => {
    if (bookmark !== null && bookmark >= 0 && bookmark < totalPages) {
      setCurrentPage(bookmark);
    } else {
      alert('بوکمارکی تنظیم نشده است.');
    }
  };

  const clearBookmark = () => {
    setBookmark(null);
    alert('بوکمارک حذف شد.');
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen"><Loader2 size={40} className="animate-spin text-green-600" /></div>;
  if (error || !product) return <div className="text-center text-red-600 p-4">{error || 'محصولی یافت نشد.'}</div>;

  const { title, cover_url } = product;

  const rawHtml = pages[currentPage] || '<p>متن موجود نیست.</p>';
  const sanitized = sanitizeHtml(rawHtml);
  const persianizedHtml = persianizeHtmlNumbers(sanitized);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-stone-100 rtl" dir="rtl">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <div className="mb-4 text-sm text-gray-600 flex items-center flex-wrap gap-1">
          <Link to="/" className="hover:text-green-700">خانه</Link>
          <ChevronLeft size={14} className="mx-1" />
          <Link to={`/products/${slug}`} className="hover:text-green-700">جزئیات</Link>
          <ChevronLeft size={14} className="mx-1" />
          <span className="text-gray-800 font-light">چکیده محتوا</span>
        </div>

        {/* Header */}
        <div className="bg-green-50/80 rounded-xl shadow-md overflow-hidden mb-6 border border-green-100">
          <div className="flex flex-row gap-4 p-4 items-center">
            {cover_url && <div className="w-16 h-16 flex-shrink-0"><img src={cover_url} alt={title} className="w-full h-full object-cover rounded-lg shadow" /></div>}
            <h1 className="text-regular md:text-[7pt] font-light text-gray-800">چکیده محتوا: {title}</h1>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 bg-green-50/60 p-3 rounded-lg shadow-sm border border-green-100">
          <div className="flex items-center gap-2">
            <button onClick={decreaseFont} disabled={fontSize <= 12} className="p-1 rounded-full hover:bg-green-100 disabled:opacity-40"><ZoomOut size={20} /></button>
            <span className="text-xs text-gray-700">{convertToPersianNumbers(fontSize)}px</span>
            <button onClick={increaseFont} disabled={fontSize >= 28} className="p-1 rounded-full hover:bg-green-100 disabled:opacity-40"><ZoomIn size={20} /></button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={setBookmarkHere} className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-md hover:bg-green-200 text-sm">
              <Bookmark size={16} /> نشانه‌گذاری این صفحه
            </button>
            {bookmark !== null && (
              <>
                <button onClick={goToBookmark} className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-md hover:bg-green-200 text-sm">
                  <Bookmark size={16} /> رفتن به نشانه (صفحه {convertToPersianNumbers(bookmark + 1)})
                </button>
                <button onClick={clearBookmark} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-md hover:bg-purple-200 text-sm">حذف نشانه</button>
              </>
            )}
          </div>
        </div>

        {totalPages > 1 && <PaginationControl currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />}

        <div className="bg-green-50/90 rounded-xl shadow-md p-6 border border-green-100">
          <div
            className="prose prose-lg max-w-none text-justify prose-headings:font-bold prose-a:text-indigo-600 prose-strong:text-gray-800 prose-ul:list-disc prose-ol:list-decimal rtl"
            style={{
              fontSize: `${fontSize}px`,
              fontFamily: "'Vazirmatn', 'Tahoma', sans-serif",
            }}
            dangerouslySetInnerHTML={{ __html: persianizedHtml }}
          />
        </div>

        {totalPages > 1 && <PaginationControl currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />}

        <div className="mt-6 text-center">
          <Link to={`/products/${slug}`} className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-green-800 bg-green-100 hover:bg-green-200 transition">بازگشت به صفحه محتوا</Link>
        </div>
      </div>
    </div>
  );
};

export default ProductSummaryPage;